import React, { useState } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import { subscriberService } from '../../services/subscriberService';

const SubscriptionContainer = styled.div`
  background: ${props => props.variant === 'footer' ? '#8b5a3a' : 'var(--subscription-bg)'};
  padding: 1.5rem;
  border-radius: 10px;
  box-shadow: ${props => props.variant === 'footer' ? 'none' : '0 3px 15px rgba(0,0,0,0.1)'};
  margin: ${props => props.variant === 'footer' ? '2rem 0' : '1rem 0'};
`;

const SubscriptionTitle = styled.h3`
  color: ${props => props.variant === 'footer' ? 'white' : 'var(--brand-primary)'};
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
`;

const SubscriptionDescription = styled.p`
  color: ${props => props.variant === 'footer' ? 'rgba(255,255,255,0.9)' : 'var(--text-secondary)'};
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.6;
`;

const SubscriptionFormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const EmailInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 2px solid ${props => props.$error ? '#e74c3c' : props.variant === 'footer' ? 'rgba(255,255,255,0.3)' : '#ddd'};
  border-radius: 5px;
  font-size: 1rem;
  background: ${props => props.variant === 'footer' ? 'rgba(255,255,255,0.9)' : 'var(--bg-input)'};
  color: #2c3e50;

  &:focus {
    outline: none;
    border-color: ${props => props.$error ? '#e74c3c' : '#c4704b'};
  }

  &::placeholder {
    color: #999;
  }
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.disabled ? '#95a5a6' : '#c4704b'};
  color: white;
  border: none;
  border-radius: 5px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 600;
  transition: all 0.3s;
  white-space: nowrap;

  &:hover {
    background: ${props => props.disabled ? '#95a5a6' : '#8b5a3a'};
  }
`;

const ConsentContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: ${props => props.variant === 'footer' ? 'rgba(255,255,255,0.9)' : 'var(--text-secondary)'};
`;

const Checkbox = styled.input`
  margin-top: 0.2rem;
  cursor: pointer;
  width: 16px;
  height: 16px;
`;

const MessageBox = styled.div`
  padding: 0.75rem;
  border-radius: 5px;
  font-size: 0.9rem;
  background: ${props => props.$success ? 'var(--success-bg)' : 'var(--error-bg)'};
  color: ${props => props.$success ? 'var(--success-text)' : 'var(--error-text)'};
  border: 1px solid ${props => props.$success ? 'var(--success-border)' : 'var(--error-border)'};
`;

const SubscriptionForm = ({ source = 'footer', variant = 'footer' }) => {
  const { t, currentLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [emailError, setEmailError] = useState('');

  // Email validation
  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  // Rate limiting check
  const checkRateLimit = () => {
    const attempts = JSON.parse(localStorage.getItem('subscriptionAttempts') || '[]');
    const oneHourAgo = Date.now() - (60 * 60 * 1000);

    // Filter attempts from last hour
    const recentAttempts = attempts.filter(timestamp => timestamp > oneHourAgo);

    if (recentAttempts.length >= 5) {
      return false; // Rate limit exceeded
    }

    // Add current attempt
    recentAttempts.push(Date.now());
    localStorage.setItem('subscriptionAttempts', JSON.stringify(recentAttempts));

    return true;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError(t('Invalid email format', 'Định dạng email không hợp lệ'));
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!email || !validateEmail(email)) {
      setMessage({
        type: 'error',
        text: t('Please enter a valid email address', 'Vui lòng nhập địa chỉ email hợp lệ')
      });
      return;
    }

    if (!gdprConsent) {
      setMessage({
        type: 'error',
        text: t('Please accept the privacy policy to continue', 'Vui lòng chấp nhận chính sách bảo mật để tiếp tục')
      });
      return;
    }

    // Rate limiting
    if (!checkRateLimit()) {
      setMessage({
        type: 'error',
        text: t(
          'Too many subscription attempts. Please try again later.',
          'Quá nhiều lần thử đăng ký. Vui lòng thử lại sau.'
        )
      });
      return;
    }

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      // Check if email already exists
      const existingSubscribers = await subscriberService.checkEmailExists(email);

      if (existingSubscribers.length > 0) {
        const subscriber = existingSubscribers[0];
        if (subscriber.status === 'active') {
          setMessage({
            type: 'error',
            text: t('This email is already subscribed', 'Email này đã đăng ký nhận tin')
          });
          setIsSubmitting(false);
          return;
        } else if (subscriber.status === 'unsubscribed') {
          // Reactivate subscription
          await subscriberService.reactivateSubscription(subscriber.id);
          setMessage({
            type: 'success',
            text: t('Welcome back! Your subscription has been reactivated.', 'Chào mừng trở lại! Đăng ký của bạn đã được kích hoạt lại.')
          });
          setEmail('');
          setGdprConsent(false);
          setIsSubmitting(false);

          // Clear success message after 5 seconds
          setTimeout(() => setMessage({ type: '', text: '' }), 5000);
          return;
        }
      }

      // Create new subscription
      const subscriberData = {
        email: email.toLowerCase().trim(),
        subscribedAt: new Date().toISOString(),
        language: currentLanguage,
        status: 'active',
        source: source,
        gdprConsent: true,
        unsubscribedAt: null,
        metadata: {
          userAgent: navigator.userAgent,
          referrer: document.referrer || 'direct'
        }
      };

      await subscriberService.createSubscriber(subscriberData);

      setMessage({
        type: 'success',
        text: t(
          'Thank you for subscribing! You will receive updates about our news and offers.',
          'Cảm ơn bạn đã đăng ký! Bạn sẽ nhận được thông tin cập nhật về tin tức và ưu đãi của chúng tôi.'
        )
      });

      setEmail('');
      setGdprConsent(false);

      // Clear success message after 5 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);

    } catch (error) {
      console.error('Subscription error:', error);
      setMessage({
        type: 'error',
        text: t(
          'Failed to subscribe. Please try again later.',
          'Không thể đăng ký. Vui lòng thử lại sau.'
        )
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SubscriptionContainer variant={variant}>
      <SubscriptionTitle variant={variant}>
        {t('Subscribe to Our Newsletter', 'Đăng ký nhận bản tin')}
      </SubscriptionTitle>
      <SubscriptionDescription variant={variant}>
        {t(
          'Get the latest news, special offers, and updates delivered to your inbox.',
          'Nhận tin tức mới nhất, ưu đãi đặc biệt và thông tin cập nhật qua email.'
        )}
      </SubscriptionDescription>

      <SubscriptionFormWrapper onSubmit={handleSubmit}>
        <EmailInputContainer>
          <EmailInput
            type="email"
            placeholder={t('Enter your email', 'Nhập email của bạn')}
            value={email}
            onChange={handleEmailChange}
            $error={emailError}
            variant={variant}
            disabled={isSubmitting}
          />
          <SubmitButton type="submit" disabled={isSubmitting || emailError}>
            {isSubmitting ? (
              t('Subscribing...', 'Đang đăng ký...')
            ) : (
              t('Subscribe', 'Đăng ký')
            )}
          </SubmitButton>
        </EmailInputContainer>

        {emailError && <MessageBox $success={false} role="alert">{emailError}</MessageBox>}

        <ConsentContainer variant={variant}>
          <Checkbox
            type="checkbox"
            checked={gdprConsent}
            onChange={(e) => setGdprConsent(e.target.checked)}
            disabled={isSubmitting}
          />
          <label>
            {t(
              'I agree to receive marketing emails and accept the privacy policy.',
              'Tôi đồng ý nhận email marketing và chấp nhận chính sách bảo mật.'
            )}
          </label>
        </ConsentContainer>

        {message.text && (
          <MessageBox $success={message.type === 'success'} role="status" aria-live="polite">
            {message.text}
          </MessageBox>
        )}
      </SubscriptionFormWrapper>
    </SubscriptionContainer>
  );
};

export default SubscriptionForm;
